import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, Star } from "@phosphor-icons/react";
import { DOC_VERSION } from "@/legal/legalDocs";

const ROLES = ["Titolare/Proprietario","CEO/AD","Direttore Generale","Direttore HR","HR Manager","HR Specialist","Talent Acquisition Manager","Recruiter","Responsabile del Personale","Responsabile Amministrativo","Office Manager","Consulente del Lavoro","Consulente HR esterno","Altro"];
const EMP_COUNTS = ["1-10","11-50","51-150","151-500","500+"];
const REGIONS = ["Abruzzo","Basilicata","Calabria","Campania","Emilia-Romagna","Friuli Venezia Giulia","Lazio","Liguria","Lombardia","Marche","Molise","Piemonte","Puglia","Sardegna","Sicilia","Toscana","Trentino-Alto Adige","Umbria","Valle d'Aosta","Veneto"];

// Pricing v4 — piani selezionabili al signup via ?plan=
const SIGNUP_PLAN_LABELS = {
  start: { name: "Start", price: "€29/mese", units: "30 analisi/mese" },
  pro: { name: "Pro", price: "€79/mese", units: "100 analisi/mese", badge: "più scelto" },
  scale: { name: "Scale", price: "€199/mese", units: "300 analisi/mese" },
};

export default function RegisterPage() {
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get("plan");
  const intendedPlan = SIGNUP_PLAN_LABELS[planParam] ? planParam : null; // null = trial 14gg default
  const [form, setForm] = useState({ first_name:"", last_name:"", company_name:"", email:"", password:"", role:"", employee_count:"", region:"" });
  // B71-TER TASK 2: 2 checkbox separati, NON pre-spuntati, obbligatori per la registrazione trial
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptVexatious, setAcceptVexatious] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const touch = (k) => setTouched(p => ({ ...p, [k]: true }));

  // BUG-71: validazione client speculare ai vincoli backend
  const errors = {
    first_name: form.first_name.trim().length < 2 ? "almeno 2 caratteri" : null,
    last_name: form.last_name.trim().length < 2 ? "almeno 2 caratteri" : null,
    company_name: form.company_name.trim().length < 2 ? "almeno 2 caratteri" : null,
    email: !form.email ? "obbligatoria" : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) ? "formato non valido" : null,
    password: !form.password ? "obbligatoria"
              : form.password.length < 8 ? "almeno 8 caratteri"
              : !/[A-Z]/.test(form.password) ? "almeno una maiuscola"
              : !/[a-z]/.test(form.password) ? "almeno una minuscola"
              : !/[0-9]/.test(form.password) ? "almeno un numero"
              : null,
  };
  const formValid = !Object.values(errors).some(e => e);
  // B71-TER TASK 2: entrambe le spunte legali sono obbligatorie per abilitare il bottone
  const legalValid = acceptTerms && acceptVexatious;
  const submitDisabled = loading || !formValid || !legalValid;

  // Password strength meter
  const pwStrength = (() => {
    const p = form.password || "";
    if (!p) return { level: 0, label: "—", cls: "bg-gray-200" };
    let score = 0;
    if (p.length >= 8) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[a-z]/.test(p)) score++;
    if (/[0-9]/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p) || p.length >= 12) score++;
    if (score <= 2) return { level: 1, label: "Debole", cls: "bg-red-500", w: "33%" };
    if (score === 3 || score === 4) return { level: 2, label: "Media", cls: "bg-amber-500", w: "66%" };
    return { level: 3, label: "Forte", cls: "bg-emerald-500", w: "100%" };
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ first_name: true, last_name: true, company_name: true, email: true, password: true });
    if (!formValid) { setError("Compila i campi richiesti correttamente"); return; }
    if (!legalValid) { setError("Devi accettare le Condizioni Generali e le clausole vessatorie per procedere"); return; }
    setError(""); setLoading(true);
    try {
      await register({
        ...form,
        intended_plan: intendedPlan || undefined,
        accept_terms: true,
        accept_vexatious: true,
        terms_version: DOC_VERSION,
      });
    }
    catch (err) {
      const status = err?.response?.status;
      const d = err?.response?.data?.detail;
      if (status === 409) { setError("Email gia registrata"); }
      else if (Array.isArray(d)) { setError(d.map(x => `${x.loc?.[1] || ""}: ${x.msg}`).join(" · ")); }
      else { setError(typeof d === "string" ? d : "Errore di registrazione"); }
    } finally { setLoading(false); }
  };

  const fieldErr = (k) => touched[k] && errors[k];

  return (
    <div className="flex h-screen" data-testid="register-page">
      <div className="w-[45%] relative overflow-hidden bg-gradient-to-br from-[#0D1F3C] via-[#142B5A] to-[#1B4FD8]">
        <div className="relative z-10 flex flex-col justify-between h-full p-12">
          <h1 className="text-white text-3xl font-bold tracking-tight">SELLY<span className="text-[#56A3F5]">®</span></h1>
          <div>
            <h2 className="text-white text-3xl font-bold leading-tight mb-4">Inizia la tua<br />prova gratuita</h2>
            <p className="text-white/70 text-base max-w-md"><strong>14 giorni gratis · 10 analisi e generazioni incluse</strong> · Nessuna carta richiesta</p>
          </div>
          <p className="text-white/30 text-xs">www.selly-hr.it</p>
        </div>
      </div>
      <div className="w-[55%] flex items-center justify-center bg-white px-12 overflow-y-auto">
        <div className="w-full max-w-[440px] py-8">
          <h2 className="text-2xl font-semibold text-[#0D1F3C] mb-1">Registrati</h2>
          <p className="text-sm text-[#0D1F3C]/50 mb-6">Crea il tuo account SELLY®</p>
          {intendedPlan && (
            <div className="bg-[#1B4FD8]/10 border border-[#1B4FD8]/30 rounded-lg p-3 mb-4 flex items-center gap-2" data-testid="intended-plan-banner">
              <Star size={18} weight="fill" className="text-[#1B4FD8]" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#0D1F3C]">
                  Piano selezionato: {SIGNUP_PLAN_LABELS[intendedPlan].name} — {SIGNUP_PLAN_LABELS[intendedPlan].price}
                </p>
                <p className="text-xs text-[#0D1F3C]/60">{SIGNUP_PLAN_LABELS[intendedPlan].units} · attivazione al termine del trial 14gg</p>
              </div>
            </div>
          )}
          {error && <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-4" data-testid="register-error">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#0D1F3C]/70 text-xs mb-1 block">Nome</Label>
                <Input data-testid="register-first-name" value={form.first_name} onChange={e=>update("first_name",e.target.value)} onBlur={()=>touch("first_name")} className={`h-10 text-sm ${fieldErr("first_name") ? "border-red-400" : "border-[#0D1F3C]/15"}`} />
                {fieldErr("first_name") && <p className="text-[11px] text-red-600 mt-0.5" data-testid="err-first-name">{errors.first_name}</p>}
              </div>
              <div>
                <Label className="text-[#0D1F3C]/70 text-xs mb-1 block">Cognome</Label>
                <Input data-testid="register-last-name" value={form.last_name} onChange={e=>update("last_name",e.target.value)} onBlur={()=>touch("last_name")} className={`h-10 text-sm ${fieldErr("last_name") ? "border-red-400" : "border-[#0D1F3C]/15"}`} />
                {fieldErr("last_name") && <p className="text-[11px] text-red-600 mt-0.5" data-testid="err-last-name">{errors.last_name}</p>}
              </div>
            </div>
            <div>
              <Label className="text-[#0D1F3C]/70 text-xs mb-1 block">Nome Azienda</Label>
              <Input data-testid="register-company" value={form.company_name} onChange={e=>update("company_name",e.target.value)} onBlur={()=>touch("company_name")} className={`h-10 text-sm ${fieldErr("company_name") ? "border-red-400" : "border-[#0D1F3C]/15"}`} />
              {fieldErr("company_name") && <p className="text-[11px] text-red-600 mt-0.5" data-testid="err-company">{errors.company_name}</p>}
            </div>
            <div>
              <Label className="text-[#0D1F3C]/70 text-xs mb-1 block">Email</Label>
              <Input data-testid="register-email" type="email" value={form.email} onChange={e=>update("email",e.target.value)} onBlur={()=>touch("email")} className={`h-10 text-sm ${fieldErr("email") ? "border-red-400" : "border-[#0D1F3C]/15"}`} />
              {fieldErr("email") && <p className="text-[11px] text-red-600 mt-0.5" data-testid="err-email">{errors.email}</p>}
            </div>
            <div>
              <Label className="text-[#0D1F3C]/70 text-xs mb-1 block flex items-center gap-1.5" title="Almeno 8 caratteri, 1 maiuscola, 1 minuscola, 1 numero">
                Password
                <span className="text-[10px] text-[#0D1F3C]/40">(8+ caratteri, 1 maiuscola, 1 minuscola, 1 numero)</span>
              </Label>
              <Input data-testid="register-password" type="password" value={form.password} onChange={e=>update("password",e.target.value)} onBlur={()=>touch("password")} className={`h-10 text-sm ${fieldErr("password") ? "border-red-400" : "border-[#0D1F3C]/15"}`} />
              {form.password && (
                <div className="mt-1 flex items-center gap-2" data-testid="pw-strength">
                  <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full ${pwStrength.cls} transition-all duration-300`} style={{ width: pwStrength.w || "0%" }} />
                  </div>
                  <span className={`text-[11px] font-medium ${pwStrength.level === 1 ? "text-red-600" : pwStrength.level === 2 ? "text-amber-600" : "text-emerald-600"}`} data-testid="pw-strength-label">
                    {pwStrength.label}
                  </span>
                </div>
              )}
              {fieldErr("password") && <p className="text-[11px] text-red-600 mt-0.5" data-testid="err-password">{errors.password}</p>}
            </div>
            <div>
              <Label className="text-[#0D1F3C]/70 text-xs mb-1 block">Ruolo</Label>
              <Select value={form.role} onValueChange={v => update("role", v)}>
                <SelectTrigger data-testid="register-role" className="h-10 border-[#0D1F3C]/15 text-sm"><SelectValue placeholder="Seleziona ruolo" /></SelectTrigger>
                <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#0D1F3C]/70 text-xs mb-1 block">N. Dipendenti</Label>
                <Select value={form.employee_count} onValueChange={v => update("employee_count", v)}>
                  <SelectTrigger data-testid="register-emp-count" className="h-10 border-[#0D1F3C]/15 text-sm"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{EMP_COUNTS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#0D1F3C]/70 text-xs mb-1 block">Regione</Label>
                <Select value={form.region} onValueChange={v => update("region", v)}>
                  <SelectTrigger data-testid="register-region" className="h-10 border-[#0D1F3C]/15 text-sm"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{REGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {/* B71-TER TASK 2: accettazione obbligatoria di Condizioni Generali + Clausole vessatorie */}
            <div className="mt-3 space-y-3 border-t border-[#0D1F3C]/10 pt-4" data-testid="legal-accept-block">
              <label className="flex items-start gap-2.5 cursor-pointer" htmlFor="cb-terms">
                <Checkbox
                  id="cb-terms"
                  checked={acceptTerms}
                  onCheckedChange={(v) => setAcceptTerms(Boolean(v))}
                  data-testid="register-accept-terms"
                  className="mt-0.5"
                />
                <span className="text-xs text-[#0D1F3C]/80 leading-relaxed">
                  Dichiaro di aver letto e di accettare le{" "}
                  <Link to="/legal/condizioni-servizio" target="_blank" rel="noopener" className="text-[#1B4FD8] underline hover:text-[#0D1F3C]" data-testid="link-condizioni">
                    Condizioni Generali del Servizio
                  </Link>{" "}
                  (che includono il{" "}
                  <Link to="/legal/dpa" target="_blank" rel="noopener" className="text-[#1B4FD8] underline hover:text-[#0D1F3C]" data-testid="link-dpa">
                    DPA
                  </Link>
                  , art. 28 GDPR) e la{" "}
                  <Link to="/legal/privacy" target="_blank" rel="noopener" className="text-[#1B4FD8] underline hover:text-[#0D1F3C]" data-testid="link-privacy">
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>
              <label className="flex items-start gap-2.5 cursor-pointer" htmlFor="cb-vexatious">
                <Checkbox
                  id="cb-vexatious"
                  checked={acceptVexatious}
                  onCheckedChange={(v) => setAcceptVexatious(Boolean(v))}
                  data-testid="register-accept-vexatious"
                  className="mt-0.5"
                />
                <span className="text-xs text-[#0D1F3C]/80 leading-relaxed">
                  Ai sensi degli artt. 1341 e 1342 c.c. approvo specificamente le clausole:
                  {" "}<strong>Art. 3</strong> (proprieta intellettuale e licenza),
                  {" "}<strong>Art. 4</strong> (uso limitato alla propria organizzazione),
                  {" "}<strong>Art. 5</strong> (divieti d'uso),
                  {" "}<strong>Art. 6</strong> (riservatezza e segreto commerciale),
                  {" "}<strong>Art. 7</strong> (clausola risolutiva espressa),
                  {" "}<strong>Art. 8</strong> (durata e recesso),
                  {" "}<strong>Art. 9</strong> (foro esclusivo di Ascoli Piceno).
                </span>
              </label>
            </div>
            <Button data-testid="register-submit-btn" type="submit" disabled={submitDisabled}
              className="w-full h-11 bg-[#1B4FD8] hover:bg-[#0D1F3C] text-white font-medium mt-2 disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? "Registrazione..." : (intendedPlan ? "Crea Account" : "Inizia la prova gratuita")}
            </Button>
          </form>
          <div className="mt-4 mb-2 flex flex-row items-center justify-center gap-2 text-sm" data-testid="social-proof">
            <span className="flex items-center gap-0.5" data-testid="social-proof-stars">
              <Star size={14} weight="fill" className="text-amber-400" />
              <Star size={14} weight="fill" className="text-amber-400" />
              <Star size={14} weight="fill" className="text-amber-400" />
              <Star size={14} weight="fill" className="text-amber-400" />
              <Star size={14} weight="fill" className="text-amber-400" />
            </span>
            <span className="text-gray-600 font-medium" data-testid="social-proof-text">4.8/5 — Gia scelto da 823 aziende italiane</span>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-500" data-testid="trust-badges">
            <span className="flex items-center gap-1.5" data-testid="trust-badge-gdpr">
              <CheckCircle size={14} weight="fill" className="text-emerald-600" />
              Dati conformi GDPR
            </span>
            <span className="flex items-center gap-1.5" data-testid="trust-badge-eu">
              <CheckCircle size={14} weight="fill" className="text-emerald-600" />
              Server in UE
            </span>
            <span className="flex items-center gap-1.5" data-testid="trust-badge-cancel">
              <CheckCircle size={14} weight="fill" className="text-emerald-600" />
              Cancellazione in 1 click
            </span>
          </div>
          {/* B82 P1.1: bottone "Registrati con Google" rimosso — vedi
              commento in LoginPage.js. */}
          <p className="text-center text-sm text-[#0D1F3C]/50 mt-4">
            Hai gia un account? <Link to="/login" className="text-[#1B4FD8] font-medium hover:underline" data-testid="login-link">Accedi</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
